import type {
	AddItemRestrictionsArgs,
	AddItemRestrictionsResult,
	CheckProductNumbersArgs,
	CheckProductNumbersResult,
} from '@ogm3/server-types';
import { push } from 'react-router-redux';
import { createFetchAction } from '../async-actions';
import {
	AddItemRestrictions,
	CheckProductNumbers,
} from '../remote/hubs/Web_Controllers_V3_RestrictionsController';

// ======
// State
// ======

export interface AddItemRestrictionsPageState {
	productNumberView: ProductNumberView[];
	productNumberData: Record<string, ProductNumberData>;
	productTextArea: string;
	messageTextArea: string;
	defaultMessage: string;
	shouldSendNextQueryRequest: boolean;
	nextQueryRequest: CheckProductNumbersArgs | null;
	nextSaveRequest: AddItemRestrictionsArgs | null;
	listId: number;
	isSaving: boolean;
	isDirty: boolean;
	canSave: boolean;
}

export type ProductNumberView = ProductNumberData & { key: string };

export function initialState(): AddItemRestrictionsPageState {
	return {
		productNumberView: [],
		productNumberData: {},
		productTextArea: '',
		messageTextArea: '',
		defaultMessage: '',
		shouldSendNextQueryRequest: false,
		nextQueryRequest: null,
		nextSaveRequest: null,
		listId: -1,
		isSaving: false,
		isDirty: false,
		canSave: false,
	};
}

export interface ProductNumberData {
	status: 'loading' | 'success' | 'failure' | 'empty';
	productNumber: string;
	description: string;
	message: string;
}

// ========
// Reducer
// ========

export function AddItemRestrictionReducer(
	state = initialState(),
	action: AddItemRestrictionAction
): AddItemRestrictionsPageState {
	switch (action.type) {
		case '@add-item-restr/ADD_ITEM_RESTRICTIONS_PAGE_LOADED': {
			// make the blank entry always exist.
			const nextData = {
				'': {
					productNumber: '',
					message: '',
					status: 'empty',
					description: '--',
				},
			} as const;

			const { listId, defaultMessage } = action.payload;

			return {
				...initialState(),
				productNumberData: nextData,
				listId,
				defaultMessage,
			};
		}

		case '@add-item-restr/PRODUCT_NUMBERS_CHANGED_PENDING': {
			const { payload = '' } = action;
			const { productNumberData, listId } = state;

			const fromProductsArea = getVisibleProducts(payload);
			const toCheck = fromProductsArea.filter(
				(x) => !Object.keys(state.productNumberData).includes(x)
			);

			const nextData = { ...productNumberData };

			for (const x of toCheck) {
				nextData[x] = {
					status: 'loading',
					productNumber: x,
					description: '--',
					message: '',
				};
			}

			const nextMessageTextArea = computeMessagesTextArea(
				fromProductsArea,
				nextData
			);

			// don't send a request if there's no products to check.
			const shouldSendNextQueryRequest = toCheck.length > 0;

			return {
				...state,
				productTextArea: payload,
				messageTextArea: nextMessageTextArea,
				productNumberData: nextData,
				shouldSendNextQueryRequest,
				nextQueryRequest: {
					baseSupplierProductNumbers: toCheck,
					restrictionListId: listId,
				},
				isDirty: true,
			};
		}

		case '@add-item-restr/PRODUCT_NUMBERS_CHANGED_SUCCESS': {
			const { payload } = action;
			const { productNumberData, productTextArea, defaultMessage } = state;

			const nextData = { ...productNumberData };

			for (const x of payload.validations) {
				nextData[x.productNumber] = {
					status: x.isValid ? 'success' : 'failure',
					productNumber: x.productNumber,
					description: x.isValid ? x.description : '--',
					message: x.isValid ? defaultMessage : '',
				};
			}

			const fromProductsArea = getVisibleProducts(productTextArea);
			const nextMessageTextArea = computeMessagesTextArea(
				fromProductsArea,
				nextData
			);

			const nextView = fromProductsArea
				.map((x) => nextData[x])
				.map((x, i) => ({ ...x, key: `${x.productNumber}-${i}` }));

			const canSave = nextView.every(
				(x) => x.status === 'success' || x.status === 'empty'
			);

			return {
				...state,
				productNumberView: nextView,
				productNumberData: nextData,
				messageTextArea: nextMessageTextArea,
				canSave,
			};
		}

		case '@add-item-restr/DEFAULT_MESSAGE_CHANGED': {
			const { payload } = action;
			const { productNumberData, productTextArea, defaultMessage } = state;

			const fromProductsArea = getVisibleProducts(productTextArea);

			const nextData = { ...productNumberData };

			const messagesToChange = Array.from(Object.values(nextData)).filter(
				(x) => x.message === defaultMessage
			);

			for (const x of messagesToChange) {
				nextData[x.productNumber] = { ...x, message: payload };
			}

			const nextMessageTextArea = computeMessagesTextArea(
				fromProductsArea,
				nextData
			);

			return {
				...state,
				productNumberData: nextData,
				messageTextArea: nextMessageTextArea,
				defaultMessage: payload,
			};
		}

		case '@add-item-restr/MESSAGES_CHANGED': {
			const { payload } = action;
			const { productNumberData, productTextArea } = state;

			const fromProductsArea = productTextArea.split('\n').map((x) => x.trim());
			const fromMessagesArea = payload.split('\n');
			const joinedData = fromProductsArea.map((x, i) => ({
				product: x,
				message: fromMessagesArea[i],
			}));

			const nextData = { ...productNumberData };
			for (const x of joinedData) {
				const existing = nextData[x.product];
				nextData[x.product] = { ...existing, message: x.message };
			}

			const nextMessageTextArea = computeMessagesTextArea(
				fromProductsArea,
				nextData
			);

			return {
				...state,
				messageTextArea: nextMessageTextArea,
				productNumberData: nextData,
			};
		}

		case '@add-item-restr/ITEM_RESTRICTIONS_SAVED_PENDING': {
			const { productNumberData, productTextArea, listId } = state;
			const productsToAdd = getVisibleProducts(productTextArea)
				.map((x) => productNumberData[x])
				.filter((x) => x.status === 'success')
				.map((x) => ({
					baseSupplierProductNumber: x.productNumber,
					message: x.message,
				}));

			const nextArgs: AddItemRestrictionsArgs = {
				restrictionListId: listId,
				items: productsToAdd,
			};

			return {
				...state,
				nextSaveRequest: nextArgs,
				canSave: false,
				isSaving: true,
			};
		}

		case '@add-item-restr/ITEM_RESTRICTIONS_SAVED_SUCCESS': {
			return initialState();
		}
	}

	return state;
}

function computeMessagesTextArea(
	visibleProducts: string[],
	productNumberData: Record<string, ProductNumberData>
): string {
	const nextMessageTextArea = visibleProducts
		.map((x) => productNumberData[x])
		.map((x) => x.message)
		.join('\n');

	return nextMessageTextArea;
}

function getVisibleProducts(productsTextArea: string): string[] {
	return productsTextArea.split('\n').map((x) => x.trim());
}

// ========
// Actions
// ========

export type AddItemRestrictionAction =
	| PRODUCT_NUMBERS_CHANGED_PENDING
	| PRODUCT_NUMBERS_CHANGED_FAILURE
	| PRODUCT_NUMBERS_CHANGED_SUCCESS
	| DEFAULT_MESSAGE_CHANGED
	| MESSAGES_CHANGED
	| ADD_ITEM_RESTRICTIONS_PAGE_LOADED
	| ITEM_RESTRICTIONS_SAVED_PENDING
	| ITEM_RESTRICTIONS_SAVED_FAILURE
	| ITEM_RESTRICTIONS_SAVED_SUCCESS;

export const productNumbersChanged = createFetchAction<
	string,
	PRODUCT_NUMBERS_CHANGED_PENDING,
	PRODUCT_NUMBERS_CHANGED_FAILURE,
	PRODUCT_NUMBERS_CHANGED_SUCCESS
>(
	'@add-item-restr/PRODUCT_NUMBERS_CHANGED_PENDING',
	'@add-item-restr/PRODUCT_NUMBERS_CHANGED_FAILURE',
	'@add-item-restr/PRODUCT_NUMBERS_CHANGED_SUCCESS',
	(args, dispatch, getState) => {
		const { nextQueryRequest, shouldSendNextQueryRequest } =
			getState().addItemRestrictions;

		if (shouldSendNextQueryRequest) {
			return CheckProductNumbers(nextQueryRequest);
		}

		return Promise.resolve({
			errors: [],
			validations: [],
		});
	},
	(args) => args
);

export interface PRODUCT_NUMBERS_CHANGED_PENDING {
	type: '@add-item-restr/PRODUCT_NUMBERS_CHANGED_PENDING';
	payload: string;
}
export interface PRODUCT_NUMBERS_CHANGED_FAILURE {
	type: '@add-item-restr/PRODUCT_NUMBERS_CHANGED_FAILURE';
	errors: string[];
}
export interface PRODUCT_NUMBERS_CHANGED_SUCCESS {
	type: '@add-item-restr/PRODUCT_NUMBERS_CHANGED_SUCCESS';
	payload: CheckProductNumbersResult;
}

export interface DEFAULT_MESSAGE_CHANGED {
	type: '@add-item-restr/DEFAULT_MESSAGE_CHANGED';
	payload: string;
}
export function defaultMessageChanged(
	payload: string
): DEFAULT_MESSAGE_CHANGED {
	return {
		type: '@add-item-restr/DEFAULT_MESSAGE_CHANGED',
		payload,
	};
}

export interface MESSAGES_CHANGED {
	type: '@add-item-restr/MESSAGES_CHANGED';
	payload: string;
}
export function messagesChanged(payload: string): MESSAGES_CHANGED {
	return { type: '@add-item-restr/MESSAGES_CHANGED', payload };
}

export interface AddItemRestrictionsPageLoadedArgs {
	listId: number;
	defaultMessage: string;
}

export interface ADD_ITEM_RESTRICTIONS_PAGE_LOADED {
	type: '@add-item-restr/ADD_ITEM_RESTRICTIONS_PAGE_LOADED';
	payload: AddItemRestrictionsPageLoadedArgs;
}

export function addItemRestrictionsPageLoaded(
	listId: number,
	defaultMessage: string
): ADD_ITEM_RESTRICTIONS_PAGE_LOADED {
	return {
		type: '@add-item-restr/ADD_ITEM_RESTRICTIONS_PAGE_LOADED',
		payload: { listId, defaultMessage },
	};
}

export interface ITEM_RESTRICTIONS_SAVED_PENDING {
	type: '@add-item-restr/ITEM_RESTRICTIONS_SAVED_PENDING';
}
export interface ITEM_RESTRICTIONS_SAVED_FAILURE {
	type: '@add-item-restr/ITEM_RESTRICTIONS_SAVED_FAILURE';
	errors: string[];
}
export interface ITEM_RESTRICTIONS_SAVED_SUCCESS {
	type: '@add-item-restr/ITEM_RESTRICTIONS_SAVED_SUCCESS';
	payload: AddItemRestrictionsResult;
}

export const itemRestrictionsSaved = createFetchAction<
	{},
	ITEM_RESTRICTIONS_SAVED_PENDING,
	ITEM_RESTRICTIONS_SAVED_FAILURE,
	ITEM_RESTRICTIONS_SAVED_SUCCESS
>(
	'@add-item-restr/ITEM_RESTRICTIONS_SAVED_PENDING',
	'@add-item-restr/ITEM_RESTRICTIONS_SAVED_FAILURE',
	'@add-item-restr/ITEM_RESTRICTIONS_SAVED_SUCCESS',
	(args, dispatch, getState) => {
		const { nextSaveRequest, listId } = getState().addItemRestrictions;
		return AddItemRestrictions(nextSaveRequest).then((res) => {
			if (res.ok) {
				dispatch(push(`/restrictions/${listId}/items`));
			}

			return res;
		});
	}
);
